// What's new (/whats-new): every published Super Portal newsletter, newest first,
// laid out as a release timeline. Source is releases/published/*.md (see
// releases/README.md), bundled at build time, so this only ever shows what is
// actually deployed. The megaphone in the top bar shows a dot until the newest
// issue has been seen.

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Chip, Typography } from '@mui/material';
import { RELEASES } from './releases.generated';

/** Unread tracking for one set of release notes: a nav dot shows until the
 *  newest issue has been seen. Per browser (localStorage), like the Super
 *  Portal's; the Integrations notes use their own key and event (#4235). */
export function releaseFeed(releases, seenKey, seenEvent) {
    const latest = releases[0]?.id || null;
    return {
        latest,
        seenEvent,
        hasUnseen() {
            if (!latest) return false;
            try { return localStorage.getItem(seenKey) !== latest; } catch { return false; }
        },
        markSeen() {
            if (!latest) return;
            try { localStorage.setItem(seenKey, latest); } catch { /* private window: dot just stays */ }
            window.dispatchEvent(new Event(seenEvent));
        },
    };
}

const SEEN_KEY = 'superportal.releases.seen';
export const RELEASES_SEEN_EVENT = 'superportal:releases-seen';
const FEED = releaseFeed(RELEASES, SEEN_KEY, RELEASES_SEEN_EVENT);
export const LATEST_RELEASE_ID = FEED.latest;

export function hasUnseenRelease() {
    return FEED.hasUnseen();
}

export function markReleasesSeen() {
    FEED.markSeen();
}

// **bold** is the only inline markup the newsletter format uses.
function Inline({ text }) {
    return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, i) => (
        part.startsWith('**') && part.endsWith('**')
            ? <Box key={i} component="strong" sx={{ color: 'text.primary', fontWeight: 700 }}>{part.slice(2, -2)}</Box>
            : <span key={i}>{part}</span>
    ));
}

function formatDate(iso) {
    const d = new Date(`${iso}T12:00:00`);
    return Number.isNaN(d.getTime()) ? iso
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Issue({ release, latest, last }) {
    return (
        <Box component="article" data-testid={`release-${release.id}`}
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '132px 1fr' }, columnGap: 4 }}>
            {/* Date rail: the date and version sit on a line that runs down the timeline */}
            <Box sx={{ pt: { sm: 0.5 }, mb: { xs: 1, sm: 0 }, display: 'flex', flexDirection: { xs: 'row', sm: 'column' },
                alignItems: { xs: 'center', sm: 'flex-end' }, gap: 1, textAlign: { sm: 'right' } }}>
                {release.date && (
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                        {formatDate(release.date)}
                    </Typography>
                )}
                {release.version && <Chip size="small" variant="outlined" label={`v${release.version}`} />}
            </Box>
            <Box sx={{ position: 'relative', pl: { sm: 4 }, pb: last ? 0 : 5,
                borderLeft: { sm: 2 }, borderColor: { sm: last ? 'transparent' : 'divider' } }}>
                <Box aria-hidden sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', left: -7, top: 8,
                    width: 12, height: 12, borderRadius: '50%', bgcolor: latest ? 'primary.main' : 'background.default',
                    border: 2, borderColor: latest ? 'primary.main' : 'divider' }} />
                {latest && (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                        color: 'primary.main', mb: 0.5 }}>
                        Latest
                    </Typography>
                )}
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1.5, textWrap: 'balance' }}>
                    {release.title}
                </Typography>
                <Box sx={{ fontSize: 16, lineHeight: 1.65, color: 'text.secondary', display: 'grid', gap: 1.5, maxWidth: '66ch',
                    '& p, & ul': { m: 0 }, '& ul': { pl: 2.5, display: 'grid', gap: 0.5 } }}>
                    {release.blocks.map((b, i) => {
                        if (b.type === 'ul') return <ul key={i}>{b.items.map((it) => <li key={it}><Inline text={it} /></li>)}</ul>;
                        if (b.type === 'img') {
                            return (
                                <Box key={i} component="img" src={b.src} alt={b.alt} loading="lazy"
                                    sx={{ display: 'block', maxWidth: '100%', height: 'auto', borderRadius: 1,
                                        border: 1, borderColor: 'divider' }} />
                            );
                        }
                        return <p key={i}><Inline text={b.text} /></p>;
                    })}
                </Box>
            </Box>
        </Box>
    );
}

const SUPER_PORTAL_INTRO = 'What changed and when, newest first. Each note says where to find the change and whether anything '
    + 'changes for you. The same notes go to the team by email.';

export default function WhatsNew({
    releases = RELEASES, onSeen = markReleasesSeen, eyebrow = 'Super Portal release notes', intro = SUPER_PORTAL_INTRO,
    footer = <Link to="/roadmap">See what's next on the roadmap →</Link>,
}) {
    useEffect(() => { onSeen(); }, [onSeen]);
    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', px: 2, py: { xs: 3, sm: 5 } }}>
            <Box component="header" sx={{ mb: { xs: 4, sm: 6 }, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'primary.main', mb: 1 }}>
                    {eyebrow}
                </Typography>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>What's new</Typography>
                <Typography sx={{ color: 'text.secondary', maxWidth: '60ch' }}>{intro}</Typography>
                {footer && <Typography sx={{ mt: 1.5, fontSize: 15 }}>{footer}</Typography>}
            </Box>
            {releases.length === 0
                ? <Typography sx={{ color: 'text.secondary' }}>No release notes have been published yet.</Typography>
                : releases.map((r, i) => <Issue key={r.id} release={r} latest={i === 0} last={i === releases.length - 1} />)}
        </Box>
    );
}
